import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@design-system/Button';
import { NavBar } from '@design-system/NavBar';
import { Dialog, DialogDescription, DialogHeader, DialogTitle } from '@design-system/Dialog';
import { ErrorLoadingPage } from '@/components/screens/ErrorHandling/ErrorLoadingPage';
import { Skeleton } from '@design-system/Skeleton';
import { useSMPData } from './hooks/useSMPData';
import { useMaintenanceGuard } from './hooks/useMaintenanceGuard';
import { useGetChangePlanData, useGetPlanComponentMap, useChangePlan } from '@viome/viome-base/src/usecase/SMP/SMPQuery';
import { useGetSubscriptionStatusQuery } from '@viome/viome-base/src/usecase/Misc/MiscQuery';
import type { SMPPlanComponent, SMPPlanMapEntry } from '@viome/viome-base/src/usecase/SMP/SMP.model';
import { SMP_PATH } from '@/constants/paths';
import { OrderInProgressDisclaimer, CheckIcon, UncheckedIcon, CheckmarkSmall, SMPDialogContent, ShimmerImage } from './components';
import { formatProductName } from './utils/smpHelpers';

type Step = 'order-in-progress' | 'product-selection' | 'order-summary' | 'confirmation';

const ProductCard = ({ product, isSelected, isDisabled, onToggle }: { product: SMPPlanComponent; isSelected: boolean; isDisabled?: boolean; onToggle: (id: number) => void }) => (
  <button onClick={() => onToggle(product.product_id)} disabled={isDisabled} className={`w-full rounded-[20px] bg-white py-5 px-5 flex items-center gap-5 transition-all text-left ${isSelected ? 'border-2 border-[#146464]' : 'border border-transparent'} ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
    <div className="flex flex-1 gap-5 items-center">
      <ShimmerImage src={product.image} alt={product.title} containerClassName="size-[80px] rounded-xl overflow-hidden bg-background shrink-0" className="size-full object-cover" />
      <div className="flex flex-col gap-3"><span className="typography-body text-primary">{formatProductName(product.title)}</span><span className="typography-caption2 text-secondary">30-day supply</span></div>
    </div>
    {isSelected ? <CheckIcon /> : <UncheckedIcon />}
  </button>
);

export const SMPAddRemoveProductsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const cancellationFlowId = (location.state as { cancellationFlowId?: number } | null)?.cancellationFlowId;
  const { subscriptionId, subscriptionDetail: sub, hasOrderInProgress, showMaintenance, isSessionReady, isLoading: isSMPLoading, isError: isSMPError } = useSMPData({
    skip: { discounts: true, changePlan: true, uiMessages: true, canSkip: true, holdingPattern: true },
  });

  useMaintenanceGuard(showMaintenance);

  const { data: changePlanData, isLoading: isLoadingPlan, isError: isChangePlanError } = useGetChangePlanData(subscriptionId, { enabled: isSessionReady && !!subscriptionId });
  const { data: planComponentMap, isLoading: isLoadingMap, isError: isMapError } = useGetPlanComponentMap({ enabled: isSessionReady });
  const changePlanMutation = useChangePlan();
  const { data: subscriptionStatusData } = useGetSubscriptionStatusQuery();
  const isConcierge = subscriptionStatusData?.subscription_details?.type === 'CONCIERGE';

  const [step, setStep] = useState<Step>(hasOrderInProgress ? 'order-in-progress' : 'product-selection');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [canSelectKit, setCanSelectKit] = useState(true);
  const [noProductsModal, setNoProductsModal] = useState(false);
  const [noPlanModal, setNoPlanModal] = useState(false);

  const currentComps = changePlanData?.current_components ?? [];
  const allowedComps = changePlanData?.allowed_components ?? [];
  const allPlanMap = planComponentMap ?? [];
  const testComps = useMemo(() => currentComps.filter((c) => c.type === 'KIT'), [currentComps]);
  const suppComps = useMemo(() => currentComps.filter((c) => c.type !== 'KIT'), [currentComps]);
  const allProducts = useMemo(() => [...currentComps, ...allowedComps], [currentComps, allowedComps]);
  const selectedProducts = useMemo(() => allProducts.filter((p) => selectedIds.includes(p.product_id)), [allProducts, selectedIds]);

  useEffect(() => { if (currentComps.length > 0 && selectedIds.length === 0) setSelectedIds(currentComps.map((c) => c.product_id)); }, [currentComps]);
  useEffect(() => { setStep(hasOrderInProgress ? 'order-in-progress' : 'product-selection'); }, [hasOrderInProgress]);

  const getPlan = useCallback((prods: SMPPlanComponent[]) => {
    const id = prods.map((c) => c.variant_id).sort((a, b) => a - b).join('');
    return allPlanMap.find((p) => p.identifier === id) ?? null;
  }, [allPlanMap]);

  useEffect(() => {
    if (!selectedProducts.length || !allPlanMap.length) return;
    const selNonKit = selectedProducts.filter((p) => p.type !== 'KIT').map((p) => p.product_id).sort().join(',');
    const curNonKit = suppComps.map((p) => p.product_id).sort().join(',');
    if (selNonKit !== curNonKit) {
      setCanSelectKit(false);
      const kitIds = testComps.map((t) => t.product_id);
      setSelectedIds((prev) => prev.filter((id) => !kitIds.includes(id)));
    } else setCanSelectKit(true);
  }, [selectedProducts.length, suppComps, testComps, allPlanMap]);

  const currentPlan = useMemo(() => currentComps.length && allPlanMap.length ? getPlan(currentComps) : null, [currentComps, allPlanMap, getPlan]);

  const targetPlan = useMemo<SMPPlanMapEntry | null>(() => {
    if (!selectedProducts.length) return null;
    return getPlan(selectedProducts);
  }, [selectedProducts, getPlan]);

  const planPrice = useMemo(() => {
    if (!selectedProducts.length) return '0.00';
    if (targetPlan && currentPlan && targetPlan.identifier === currentPlan.identifier) { const lp = sub?.line_items?.[0]?.price; if (lp) return `${lp / 100}`; }
    return targetPlan?.plan_product?.price ?? '-1';
  }, [selectedProducts, currentPlan, targetPlan, sub]);

  const toggleProduct = useCallback((id: number) => setSelectedIds((prev) => prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]), []);
  const isConfirmDisabled = !selectedIds.length || (currentPlan && targetPlan && currentPlan.identifier === targetPlan.identifier);
  const goBack = () => navigate(SMP_PATH);
  const isLoading = isSMPLoading || isLoadingPlan || isLoadingMap;

  if (isLoading) return <section className="page-container flex flex-col gap-12 pb-24"><NavBar returnButtonTitle="Back" className="md:-ml-9" /><Skeleton className="h-8 w-64" /><Skeleton className="h-32 w-full rounded-xl" /><Skeleton className="h-32 w-full rounded-xl" /></section>;
  if (isSMPError || isChangePlanError || isMapError || !sub) return <ErrorLoadingPage />;

  const handleConfirmSelection = () => { if (!selectedIds.length) { setNoProductsModal(true); return; } if (planPrice === '-1') { setNoPlanModal(true); return; } setStep('order-summary'); };
  const handleSubmit = () => {
    if (!targetPlan) return;
    changePlanMutation.mutate({ subscriptionId, components: targetPlan.components.map((c) => ({ sku: c.sku, product_id: c.product_id, variant_id: c.variant_id })), ...(cancellationFlowId ? { cancellationFlowId } : {}) }, { onSuccess: () => setStep('confirmation'), onError: () => setNoPlanModal(true) });
  };

  const ProductSection = ({ title, products, disabled }: { title: string; products: SMPPlanComponent[]; disabled?: boolean }) => products.length ? (
    <div className="flex flex-col gap-6">
      <h2 className={`${title === 'Add New Products to My Plan' ? 'typography-h1' : 'typography-h2'} text-primary px-1`}>{title}</h2>
      <div className={`flex flex-col gap-3 ${disabled ? 'opacity-50' : ''}`}>
        {products.map((p) => <ProductCard key={p.product_id} product={p} isSelected={selectedIds.includes(p.product_id)} isDisabled={disabled} onToggle={toggleProduct} />)}
      </div>
      {title === 'My Test' && isConcierge && <p className="typography-body text-primary max-w-md">{canSelectKit ? 'You can choose to order a retest kit at any time. If you make changes to your current subscription, the retest kit included with this plan may no longer be available.' : 'Retest is not available with the selected products.'}</p>}
    </div>
  ) : null;

  return (
    <section className="page-container flex flex-col gap-6 pb-24">
      <NavBar returnButtonTitle="Back" className="md:-ml-9" onClick={step === 'order-summary' ? () => setStep('product-selection') : goBack} />

      {step === 'order-in-progress' && sub.lacore_order && (
        <OrderInProgressDisclaimer lacoreOrder={sub.lacore_order} planName={sub.line_items?.[0]?.product_name} productImage={sub.line_items?.[0]?.image} onContinue={() => setStep('product-selection')} onCancel={goBack} />
      )}

      {step === 'product-selection' && (
        <>
          <h1 className="typography-h6 text-primary">My Viome Plan</h1>
          <div className="flex flex-col lg:flex-row gap-8">
            <div className="flex-1 flex flex-col gap-12">
              <ProductSection title="My Test" products={testComps} disabled={!canSelectKit} />
              <ProductSection title="My Products" products={suppComps} />
              <ProductSection title="Add New Products to My Plan" products={allowedComps} />
            </div>
            <div className="w-full lg:w-[361px] shrink-0">
              <div className="bg-white rounded-[20px] p-5 flex flex-col gap-6 sticky top-6">
                <div className="flex flex-col items-center pt-6 gap-2">
                  <div className="flex items-baseline gap-1 text-[#146464]"><span className="typography-h2 font-medium">${planPrice === '-1' ? '--' : planPrice}</span><span className="typography-h3 font-medium">/ month</span><span className="typography-caption1">USD</span></div>
                  <span className="typography-caption2 text-tertiary">Plus tax, if applicable</span>
                </div>
                <div className="flex flex-col gap-4">
                  <Button onClick={handleConfirmSelection} disabled={!!isConfirmDisabled}>Confirm</Button>
                  <Button variant="secondary" onClick={goBack}>Cancel changes</Button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {step === 'order-summary' && (
        <>
          <h1 className="typography-d1 text-primary">My Monthly Subscription</h1>
          <div className="flex flex-col gap-4 mt-6">
            {selectedProducts.map((p) => (
              <div key={p.product_id} className="flex items-center gap-5 py-3">
                <ShimmerImage src={p.image} alt={p.title} containerClassName="size-[80px] rounded-xl overflow-hidden bg-background shrink-0" className="size-full object-cover" />
                <div className="flex flex-col gap-1"><span className="typography-body text-primary">{formatProductName(p.title)}</span><span className="typography-caption2 text-secondary">30-day supply</span></div>
              </div>
            ))}
          </div>
          <div className="border-t border-stroke-secondary-subtle my-6" />
          <div className="flex justify-between items-start"><span className="typography-title text-primary">Total:</span><div className="flex flex-col items-end"><span className="text-base/snug text-primary"><span className="font-medium">${planPrice === '-1' ? '--' : planPrice}</span> <span className="font-light">/ month</span></span><span className="typography-caption2 text-tertiary">plus tax, if applicable</span></div></div>
          {isConcierge && <p className="typography-body text-primary mt-8 max-w-xl">You can choose to order a retest kit at any time. If you make changes to your current subscription, the retest kit included with this plan may no longer be available.</p>}
          <p className="typography-caption1 text-tertiary mt-4 max-w-xl">If you have a discount remaining on your current plan, that discount will carry over to this new plan.</p>
          <div className="flex flex-col gap-4 w-full max-w-sm mt-8">
            <Button onClick={handleSubmit} disabled={changePlanMutation.isPending}>{changePlanMutation.isPending ? 'Processing...' : 'Yes'}</Button>
            <Button variant="secondary" onClick={() => setStep('product-selection')} disabled={changePlanMutation.isPending}>Cancel</Button>
          </div>
        </>
      )}

      {step === 'confirmation' && targetPlan && (
        <div className="max-w-md">
          <h1 className="typography-d1 text-primary">Confirmation</h1>
          <div className="mt-6">
            <ShimmerImage src={targetPlan.plan_product?.image} alt={targetPlan.plan_product?.title} className="w-full" />
          </div>
          <div className="mt-6">
            <p className="typography-body text-primary mb-4">Your are now subscribed to:</p>
            {targetPlan.components.map((c) => <div key={c.product_id} className="flex items-center gap-3 mb-3"><CheckmarkSmall /><span className="typography-body text-primary">{formatProductName(c.title)}</span></div>)}
          </div>
          {targetPlan.plan_product?.price && <div className="mt-4 text-[#146464] font-medium text-lg/snug"><span>${targetPlan.plan_product.price}</span><span> / month</span></div>}
          <div className="mt-8 w-full"><Button onClick={goBack} className="w-full">OK</Button></div>
        </div>
      )}

      <Dialog open={noProductsModal} onOpenChange={setNoProductsModal}>
        <SMPDialogContent>
          <DialogHeader className="gap-6 text-start">
            <DialogTitle className="typography-h3 text-primary">No Products Selected</DialogTitle>
            <DialogDescription className="typography-body text-secondary">
              Please select at least one product to continue with your subscription.
            </DialogDescription>
          </DialogHeader>
          <Button onClick={() => setNoProductsModal(false)}>OK</Button>
        </SMPDialogContent>
      </Dialog>
      <Dialog open={noPlanModal} onOpenChange={setNoPlanModal}>
        <SMPDialogContent>
          <DialogHeader className="gap-6 text-start">
            <DialogTitle className="typography-h3 text-primary">Combination Not Available</DialogTitle>
            <DialogDescription className="typography-body text-secondary">
              This combination of products is not available. Please select a different combination.
            </DialogDescription>
          </DialogHeader>
          <Button
            onClick={() => {
              setNoPlanModal(false);
              if (step !== 'product-selection') setStep('product-selection');
              setSelectedIds(currentComps.map((c) => c.product_id));
            }}
          >
            OK
          </Button>
        </SMPDialogContent>
      </Dialog>
    </section>
  );
};
